import { useState, useEffect, useCallback, useRef, useId } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare, Star, X, Send, CheckCircle, Loader2, AlertTriangle, ChevronDown, Sparkles } from 'lucide-react';
import api from '../../services/api';
import './feedback.css';
import useAuthStore from '../../store/authStore';
import useNavStore from '../../store/navStore';

/**
 * FeedbackButton — bouton flottant + modal enrichie.
 *
 * Champs :
 *  - rating étoiles 1→5 (note globale, obligatoire)
 *  - NPS 0→10 (recommandation, optionnel)
 *  - content_quality 1→5 étoiles (qualité du contenu, optionnel)
 *  - favorite_features multi-select (optionnel)
 *  - had_blocking_bug Oui/Non (optionnel)
 *  - 3 champs texte libres (optionnels)
 *
 * Audience contrôlée via GET /user/feedback.php (côté serveur).
 */

const FEATURE_OPTIONS = [
    { key: 'quiz_subject',    label: 'Quiz par sous-sujet' },
    { key: 'apprentissage',   label: 'Mode apprentissage' },
    { key: 'melange',         label: 'Examen mélangé' },
    { key: 'smart_quiz',      label: 'Quiz intelligent (révision)' },
    { key: 'errors_quiz',     label: 'Quiz sur mes erreurs' },
    { key: 'history',         label: 'Historique des sessions' },
    { key: 'progression',     label: 'Progression / stats' },
    { key: 'mes_thematiques', label: 'Mes Thématiques (perso)' },
    { key: 'share_codes',     label: 'Codes de partage' },
];

export default function FeedbackButton() {
    const { user } = useAuthStore();
    const location = useLocation();
    const fabHidden = useNavStore(s => s.feedbackFabHidden);
    const dialogRef = useRef(null);
    const titleId = useId();
    const [visible, setVisible] = useState(false);
    const [open, setOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [nps, setNps] = useState(null);
    const [contentQuality, setContentQuality] = useState(0);
    const [hoverContentQuality, setHoverContentQuality] = useState(0);
    const [favoriteFeatures, setFavoriteFeatures] = useState([]);
    const [hadBlockingBug, setHadBlockingBug] = useState(null); // null | true | false
    const [pricePerception, setPricePerception] = useState(0);
    const [hoverPrice, setHoverPrice] = useState(0);
    const [activeSubs, setActiveSubs] = useState([]); // [{scope_label, duree, current_prices, paid_price, is_gift}]
    const [consentPublic, setConsentPublic] = useState(true); // pré-rempli à Oui
    const [whatWorks, setWhatWorks] = useState('');
    const [whatBlocks, setWhatBlocks] = useState('');
    const [whatToAdd, setWhatToAdd] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user) { setVisible(false); return; }
        const isAdminRoute = location.pathname.startsWith('/gestion');
        if (isAdminRoute) { setVisible(false); return; }
        api.get('/user/feedback.php')
            .then(({ data }) => {
                setVisible(!!data.data?.can_submit);
                setActiveSubs(data.data?.active_subs || []);
            })
            .catch(() => setVisible(false));
    }, [user, location.pathname]);

    // Écoute l'event 'feedback:open' déclenché par FeedbackPopup quand l'user clique "Donner mon avis"
    useEffect(() => {
        const handler = () => { if (visible) setOpen(true); };
        window.addEventListener('feedback:open', handler);
        return () => window.removeEventListener('feedback:open', handler);
    }, [visible]);

    const reset = useCallback(() => {
        setRating(0);
        setHoverRating(0);
        setNps(null);
        setContentQuality(0);
        setHoverContentQuality(0);
        setFavoriteFeatures([]);
        setHadBlockingBug(null);
        setPricePerception(0);
        setHoverPrice(0);
        setConsentPublic(true);
        setWhatWorks('');
        setWhatBlocks('');
        setWhatToAdd('');
        setSubmitted(false);
        setError(null);
    }, []);

    const handleClose = () => {
        setOpen(false);
        setTimeout(reset, 300);
    };

    const toggleFeature = (key) => {
        setFavoriteFeatures(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const handleSubmit = async () => {
        if (submitting) return;
        if (rating < 1) { setError('Sélectionne une note globale d\'abord'); return; }
        const hasText = whatWorks.trim() || whatBlocks.trim() || whatToAdd.trim();
        if (rating >= 4 && !hasText && nps === null) {
            setError('Écris au moins quelques mots ou indique si tu recommanderais monquiz à un proche.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await api.post('/user/feedback.php', {
                action: 'submit',
                rating,
                nps_score: nps,
                content_quality: contentQuality > 0 ? contentQuality : null,
                favorite_features: favoriteFeatures.length > 0 ? favoriteFeatures : null,
                had_blocking_bug: hadBlockingBug,
                price_perception: pricePerception > 0 ? pricePerception : null,
                consent_public_display: consentPublic,
                what_works: whatWorks,
                what_blocks: whatBlocks,
                what_to_add: whatToAdd,
                page_context: location.pathname + location.search,
            });
            setSubmitted(true);
            setTimeout(handleClose, 2400);
        } catch (e) {
            setError(e.response?.data?.message || 'Erreur lors de l\'envoi');
        }
        setSubmitting(false);
    };

    useEffect(() => {
        setError(null);
    }, [rating, nps, contentQuality, favoriteFeatures, hadBlockingBug, pricePerception, consentPublic, whatWorks, whatBlocks, whatToAdd]);

    useEffect(() => {
        if (!open || !visible) return;
        const dialog = dialogRef.current;
        dialog.showModal();
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            dialog.close();
            document.body.style.overflow = previousOverflow;
        };
    }, [open, visible]);

    if (!visible) return null;

    const uniqueSubs = Object.values(activeSubs.reduce((acc, sub) => {
        if (!acc[sub.scope_label]) acc[sub.scope_label] = sub;
        return acc;
    }, {}));

    return (
        <>
            {!fabHidden && <button type="button" className="feedback-fab mq-feedback-fab"
                onClick={() => setOpen(true)} title="Donner mon avis sur monquiz" aria-label="Donner mon avis">
                <MessageSquare size={18} /><span className="feedback-fab__label">Donner mon avis</span>
            </button>}

            {open && (
                <dialog ref={dialogRef} className="mq-feedback" aria-labelledby={titleId}
                    onCancel={e => { e.preventDefault(); handleClose(); }}
                    onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
                    <header className="mq-feedback__header">
                        <span className="mq-feedback__icon"><MessageSquare size={22} /></span>
                        <div>
                            <p className="mq-feedback__eyebrow">On améliore monquiz ensemble</p>
                            <h2 id={titleId}>Ton avis compte</h2>
                        </div>
                        <button type="button" className="mq-feedback__close" onClick={handleClose} aria-label="Fermer">
                            <X size={20} />
                        </button>
                    </header>

                    {submitted ? (
                        <div className="mq-feedback__success" role="status">
                            <CheckCircle size={48} />
                            <h3>Merci pour ton retour !</h3>
                            <p>Ton message a été enregistré. Tous les retours sont lus.</p>
                        </div>
                    ) : (
                        <form className="mq-feedback__form" onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
                            <div className="mq-feedback__scroll">
                                <p className="mq-feedback__intro">Quelques questions pour aider l’équipe monquiz à améliorer le site. Commence par ton impression, puis ajoute les précisions que tu souhaites.</p>
                                <p className="mq-feedback__hint">Seule la note globale est obligatoire. Pour une note de 4 ou 5, ajoute aussi quelques mots ou une note de recommandation.</p>

                                <div className="mq-feedback__rating">
                                    <Section title="Note globale" required>
                                        <StarRow value={rating} hover={hoverRating} onHover={setHoverRating} onChange={setRating} />
                                        <p className="mq-feedback__rating-caption" aria-live="polite">
                                            {rating ? ['Très décevant', 'Peu satisfaisant', 'Correct', 'Très bien', 'Excellent'][rating - 1] : 'Comment se passe ton expérience ?'}
                                        </p>
                                    </Section>
                                </div>

                                <Section title="Tu recommanderais monquiz à un proche ?">
                                    <p className="mq-feedback__hint">Note de 0 (jamais) à 10 (sans hésiter)</p>
                                    <NpsRow value={nps} onChange={setNps} />
                                </Section>

                                <Field label="Ce qui marche bien" placeholder="Ce que tu apprécies, ce qui te sert..." value={whatWorks} onChange={setWhatWorks} />

                                <div className="mq-feedback__details-heading"><Sparkles size={15} /> Envie d’ajouter des précisions ? <span>Facultatif</span></div>
                                <Details title="Les quiz et les fonctionnalités" description="Qualité des questions, outils utiles et bugs">
                                    <Section title="Qualité des questions (justesse, pertinence)">
                                        <StarRow value={contentQuality} hover={hoverContentQuality} onHover={setHoverContentQuality} onChange={setContentQuality} />
                                    </Section>
                                    <Section title="Quelles fonctionnalités t’ont servi le plus ?">
                                        <p className="mq-feedback__hint">Coche toutes celles que tu as utilisées et appréciées</p>
                                        <div className="mq-feedback__chips">
                                            {FEATURE_OPTIONS.map(f => (
                                                <button key={f.key} type="button" className="mq-feedback__choice" aria-pressed={favoriteFeatures.includes(f.key)} onClick={() => toggleFeature(f.key)}>
                                                    {favoriteFeatures.includes(f.key) && <span aria-hidden="true">✓ </span>}{f.label}
                                                </button>
                                            ))}
                                        </div>
                                    </Section>
                                    <Section title="As-tu rencontré un bug bloquant ?">
                                        <div className="mq-feedback__choices">
                                            <YesNoBtn label="Non" active={hadBlockingBug === false} onClick={() => setHadBlockingBug(false)} />
                                            <YesNoBtn label="Oui" active={hadBlockingBug === true} onClick={() => setHadBlockingBug(true)} />
                                        </div>
                                        {hadBlockingBug === true && <p className="mq-feedback__bug" role="status"><AlertTriangle size={15} /> Décris-le dans « Ce qui te bloque » dans le bloc « Tes difficultés et tes idées » ci-dessous.</p>}
                                    </Section>
                                </Details>

                                {uniqueSubs.length > 0 && <Details title="Le rapport qualité / prix" description="Tes thématiques actives et leurs tarifs publics">
                                    <Section title="Le prix te semble-t-il cohérent avec le contenu ?">
                                        <p className="mq-feedback__hint">Tes thématiques actives et leurs tarifs publics :</p>
                                        <div className="mq-feedback__prices">
                                            {uniqueSubs.map(sub => {
                                                const prices = [];
                                                if (sub.current_prices?.['1m']) prices.push(`1 mois ${sub.current_prices['1m']}€`);
                                                if (sub.current_prices?.['6m']) prices.push(`6 mois ${sub.current_prices['6m']}€`);
                                                if (sub.current_prices?.['12m']) prices.push(`12 mois ${sub.current_prices['12m']}€`);
                                                return <div key={sub.scope_label}><strong>{sub.scope_label}</strong><span>{prices.length ? prices.join(' · ') : 'Tarifs non publics'}</span></div>;
                                            })}
                                        </div>
                                        <p className="mq-feedback__hint"><strong>1 ★</strong> = trop cher pour ce que ça apporte · <strong>5 ★</strong> = très bon rapport qualité/prix</p>
                                        <StarRow value={pricePerception} hover={hoverPrice} onHover={setHoverPrice} onChange={setPricePerception} />
                                    </Section>
                                </Details>}

                                <Details title="Tes difficultés et tes idées" description="Ce qui te freine et ce que tu aimerais trouver">
                                    <Field label="Ce qui te bloque ou te frustre" placeholder="Bugs, incompréhensions, frictions..." value={whatBlocks} onChange={setWhatBlocks} />
                                    <Field label="Ce qui te manque le plus" placeholder="Une idée, une fonction utile pour ta prépa ?" value={whatToAdd} onChange={setWhatToAdd} />
                                </Details>

                                <div className="mq-feedback__consent">
                                    <Section title="J’autorise la publication de cet avis avec mon prénom sur monquiz.fr">
                                        <div className="mq-feedback__choices">
                                            <YesNoBtn label="Oui" tone="yes" active={consentPublic === true} onClick={() => setConsentPublic(true)} />
                                            <YesNoBtn label="Non" tone="no" active={consentPublic === false} onClick={() => setConsentPublic(false)} />
                                        </div>
                                        <p className="mq-feedback__hint">Seul ton prénom apparaîtrait, jamais ton email. Tu peux retirer ton consentement à tout moment en nous contactant.</p>
                                    </Section>
                                </div>
                            </div>
                            <footer className="mq-feedback__footer">
                                {error && <p className="mq-feedback__error" role="alert">{error}</p>}
                                <div className="mq-feedback__actions">
                                    <button type="button" className="mq-feedback__cancel" onClick={handleClose}>Annuler</button>
                                    <button type="submit" className="mq-feedback__send" disabled={submitting || rating < 1}>
                                        {submitting ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                                        {submitting ? 'Envoi en cours…' : 'Envoyer mon avis'}
                                    </button>
                                </div>
                            </footer>
                        </form>
                    )}
                </dialog>
            )}
        </>
    );
}

function Section({ title, required, children }) {
    return <fieldset className="mq-feedback__section">
        <legend>{title}{required && <span className="mq-feedback__required"> *</span>}</legend>
        {children}
    </fieldset>;
}

function Details({ title, description, children }) {
    return <details className="mq-feedback__details">
        <summary><span><strong>{title}</strong><small>{description}</small></span><ChevronDown size={18} /></summary>
        <div className="mq-feedback__details-content">{children}</div>
    </details>;
}

function StarRow({ value, hover, onHover, onChange }) {
    return <div className="mq-feedback__stars" onMouseLeave={() => onHover(0)}>
        {[1, 2, 3, 4, 5].map(n => <button key={n} type="button" aria-label={`${n} sur 5`} aria-pressed={value === n}
            className={(hover || value) >= n ? 'is-filled' : ''}
            onMouseEnter={() => onHover(n)} onFocus={() => onHover(n)} onBlur={() => onHover(0)} onClick={() => onChange(n)}>
            <Star size={28} fill={(hover || value) >= n ? 'currentColor' : 'none'} />
        </button>)}
    </div>;
}

function NpsRow({ value, onChange }) {
    return <div className="mq-feedback__nps">
        {Array.from({ length: 11 }, (_, n) => <button key={n} type="button" aria-label={`${n} sur 10`} aria-pressed={value === n}
            className="mq-feedback__choice" onClick={() => onChange(n)}>{n}</button>)}
    </div>;
}

function YesNoBtn({ label, active, onClick, tone }) {
    return <button type="button" className={`mq-feedback__choice${tone ? ` mq-feedback__choice--${tone}` : ''}`} aria-pressed={active} onClick={onClick}>{label}</button>;
}

function Field({ label, placeholder, value, onChange }) {
    const id = useId();
    return <div className="mq-feedback__field">
        <label htmlFor={id}>{label}</label>
        <textarea id={id} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2} />
    </div>;
}
